/**
 * Path routes.
 *
 * Explains routing behavior, guards, and response semantics.
 */
import type { Section } from '../../../types'
import { isAllowedSection } from '../../../config/sections'
import {
  DASHBOARD_ACCOUNT_HOME_PATH_REGEX,
  DASHBOARD_ACCOUNT_AUDIT_PATH_REGEX,
  DASHBOARD_ACCOUNT_PREFERENCES_PATH_REGEX,
  DASHBOARD_ACCOUNT_PRIVACY_PATH_REGEX,
  DASHBOARD_ACCOUNT_PROFILE_PATH_REGEX,
  DASHBOARD_ACCOUNT_SECURITY_PATH_REGEX,
  DASHBOARD_ACCOUNT_SESSIONS_PATH_REGEX,
  DASHBOARD_ACCOUNT_TEAM_EDIT_PATH_REGEX,
  DASHBOARD_ACCOUNT_TEAM_PATH_REGEX,
  DASHBOARD_COMMUNICATIONS_EMAIL_TEMPLATES_PATH_REGEX,
  DASHBOARD_HOME_PATH_REGEX,
  DASHBOARD_SECTION_PATH_REGEX,
  DASHBOARD_SYSTEM_EMAIL_LOGS_PATH_REGEX,
} from './constants'

const SHOP_PRODUCT_TYPES_PATH_REGEX = /^\/shop-product-types(?:\/([^/?#]+)(?:\/fields\/([^/?#]+))?)?\/?$/
const SHOP_PRODUCTS_LIST_PATH_REGEX = /^\/products\/?$/
const SHOP_PRODUCT_NEW_PATH_REGEX = /^\/products\/new\/?$/
const SHOP_PRODUCT_EDIT_PATH_REGEX = /^\/products\/([^/?#]+)\/edit\/?$/
const LEGACY_ORDER_DETAIL_PATH_REGEX = /^\/admin\/orders\/([^/?#]+)\/?$/

/**
 * Normalize browser paths from the pre-canonical `/admin/...` route scheme.
 * API endpoints intentionally keep their `/admin/...` prefix; this helper is
 * only used by the client-side browser router.
 */
export const normalizeLegacyAdminPath = (pathname: string): string => {
  if (!pathname.startsWith('/admin')) return pathname
  if (pathname === '/admin') return '/'
  if (pathname.startsWith('/admin/account')) return pathname.slice('/admin'.length)
  if (pathname === '/admin/audit-logs') return '/account/audit'
  if (pathname.startsWith('/admin/shop-product-types')) {
    return pathname.slice('/admin'.length)
  }
  if (pathname.startsWith('/admin/customers')) return pathname.slice('/admin'.length)
  if (pathname.startsWith('/admin/shop-orders')) return pathname.slice('/admin'.length)
  if (pathname === '/admin/plugins') return '/plugins'
  if (LEGACY_ORDER_DETAIL_PATH_REGEX.test(pathname)) return '/shop-orders'
  return pathname
}

export const decodePathSection = (value: string): string => {
  try {
    return decodeURIComponent(value).trim()
  } catch {
    return value.trim()
  }
}

export const resolveSectionFromDashboardPath = (pathname: string): Section | null => {
  pathname = normalizeLegacyAdminPath(pathname)
  if (DASHBOARD_HOME_PATH_REGEX.test(pathname)) return 'dashboard'
  if (DASHBOARD_COMMUNICATIONS_EMAIL_TEMPLATES_PATH_REGEX.test(pathname)) {
    return 'site-email-templates'
  }
  if (DASHBOARD_SYSTEM_EMAIL_LOGS_PATH_REGEX.test(pathname)) {
    return 'site-email-logs'
  }
  if (DASHBOARD_ACCOUNT_HOME_PATH_REGEX.test(pathname)) return 'profile-contact'
  if (DASHBOARD_ACCOUNT_PROFILE_PATH_REGEX.test(pathname)) return 'profile-contact'
  if (DASHBOARD_ACCOUNT_SECURITY_PATH_REGEX.test(pathname)) return 'profile-security'
  if (DASHBOARD_ACCOUNT_SESSIONS_PATH_REGEX.test(pathname)) return 'profile-sessions'
  if (DASHBOARD_ACCOUNT_AUDIT_PATH_REGEX.test(pathname)) return 'audit-logs'
  if (DASHBOARD_ACCOUNT_PRIVACY_PATH_REGEX.test(pathname)) return 'profile-danger'
  if (DASHBOARD_ACCOUNT_PREFERENCES_PATH_REGEX.test(pathname)) return 'profile-addresses'
  if (DASHBOARD_ACCOUNT_TEAM_PATH_REGEX.test(pathname)) return 'profile-team'
  if (DASHBOARD_ACCOUNT_TEAM_EDIT_PATH_REGEX.test(pathname)) return 'profile-team-edit'
  if (SHOP_PRODUCT_TYPES_PATH_REGEX.test(pathname)) return 'shop-product-types'
  if (SHOP_PRODUCTS_LIST_PATH_REGEX.test(pathname)) return 'shop-products'
  if (SHOP_PRODUCT_NEW_PATH_REGEX.test(pathname)) return 'shop-products'
  if (SHOP_PRODUCT_EDIT_PATH_REGEX.test(pathname)) return 'shop-products'

  const dashboardMatch = pathname.match(DASHBOARD_SECTION_PATH_REGEX)
  if (!dashboardMatch?.[1]) return null

  const section = decodePathSection(dashboardMatch[1])
  if (!section) return null
  return isAllowedSection(section) ? section : null
}

export const isShopProductTypesPath = (pathname: string): boolean =>
  SHOP_PRODUCT_TYPES_PATH_REGEX.test(normalizeLegacyAdminPath(pathname))

export const isShopProductEditorPath = (pathname: string): boolean =>
  SHOP_PRODUCT_NEW_PATH_REGEX.test(normalizeLegacyAdminPath(pathname)) ||
  SHOP_PRODUCT_EDIT_PATH_REGEX.test(normalizeLegacyAdminPath(pathname))

export const resolveShopProductIdFromPath = (pathname: string): string | null => {
  pathname = normalizeLegacyAdminPath(pathname)
  const productEditMatch = pathname.match(SHOP_PRODUCT_EDIT_PATH_REGEX)
  if (productEditMatch?.[1]) {
    const productId = decodePathSection(productEditMatch[1])
    return productId || null
  }

  const dashboardMatch = pathname.match(DASHBOARD_SECTION_PATH_REGEX)
  if (!dashboardMatch?.[1]) return null

  const section = decodePathSection(dashboardMatch[1])
  if (section !== 'shop-products') return null
  if (!dashboardMatch[2]) return null

  const productId = decodePathSection(dashboardMatch[2])
  return productId || null
}

export const resolveTeamEditUserIdFromPath = (pathname: string): string | null => {
  pathname = normalizeLegacyAdminPath(pathname)
  const teamEditMatch = pathname.match(DASHBOARD_ACCOUNT_TEAM_EDIT_PATH_REGEX)
  if (!teamEditMatch?.[1]) return null
  const userId = decodePathSection(teamEditMatch[1])
  return userId || null
}
